import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { getStarsSettlement, StarsSettlement } from '../../../api/AdminAPI';
import { botLabel, ownerLabel } from '../botLabels';

/**
 * Read what each bot took in Telegram Stars over a period.
 *
 * Spec: specs/active/2026-09-02-mirror-stars-enablement (US3, plan step 9).
 *
 * Split out of the MirrorStars screen: the two are rendered together but share
 * no state, and one screen per folder is this repo's convention. Settlement
 * itself is manual — this report only produces the numbers to settle from.
 *
 * Two properties it exists to hold, both about money:
 *
 * - **A failed read is never zeros.** Zeros and "could not read" must not look
 *   alike; one of those two means somebody is not being paid. A failure
 *   replaces the table with the reason.
 * - **The unattributed bucket is its own row.** Those Stars were taken before
 *   attribution shipped, or by a payment that named no bot. Folding them into
 *   a bot would make that bot's settlement wrong; omitting them would make the
 *   period's total quietly wrong.
 *
 * The period is asked of the server rather than filtered on screen: a
 * plausible report for the wrong dates is the failure mode to avoid.
 */

/** Local-calendar ISO day. `toISOString()` would shift the date by the timezone. */
const isoDay = (value: Date): string =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(
    value.getDate()
  ).padStart(2, '0')}`;

/** Length of the default window in days, inclusive of today. Must match the
 * server's `DEFAULT_PERIOD_DAYS` — if the two drift, the screen opens on one
 * period and labels it with another. */
export const DEFAULT_PERIOD_DAYS = 30;

/** The report's default window, matching the server's: the last 30 days.
 *
 * Chosen over month-to-date on 2026-09-04: month-to-date shows a single day on
 * the 1st, which reads as "nobody paid anything" on exactly the morning someone
 * is most likely to be settling up for the month that just ended.
 */
const defaultPeriodStart = (): string => {
  const now = new Date();
  return isoDay(
    new Date(now.getFullYear(), now.getMonth(), now.getDate() - (DEFAULT_PERIOD_DAYS - 1))
  );
};

const today = (): string => isoDay(new Date());

const formatStars = (value: number): string => `${Math.round(value).toLocaleString()} ⭐`;

const formatUsd = (value: number): string =>
  `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const StarsSettlementPanel = () => {
  const apiKey = useSelector((state: RootState) => state.userData.apiKey);

  const [fromDraft, setFromDraft] = useState(defaultPeriodStart);
  const [toDraft, setToDraft] = useState(today);
  const [period, setPeriod] = useState(() => ({
    from: defaultPeriodStart(),
    to: today(),
  }));
  const [settlement, setSettlement] = useState<StarsSettlement | null>(null);
  const [settlementLoading, setSettlementLoading] = useState(true);
  const [settlementError, setSettlementError] = useState<string | null>(null);

  const loadSettlement = useCallback(async () => {
    if (!apiKey) return;
    setSettlementLoading(true);
    const res = await getStarsSettlement(apiKey, period.from, period.to);
    if (res.success && res.data) {
      setSettlement(res.data);
      setSettlementError(null);
    } else {
      // Zeros and "could not read" must not look alike. One of those two means
      // somebody is not being paid.
      setSettlement(null);
      setSettlementError(res.error || 'Could not load the settlement report');
    }
    setSettlementLoading(false);
  }, [apiKey, period]);

  useEffect(() => {
    loadSettlement();
  }, [loadSettlement]);

  const unattributed = settlement?.unattributed;
  const hasUnattributed =
    !!unattributed && (unattributed.stars_total > 0 || unattributed.usd_total > 0);

  return (
    <Box>
      <Heading size="md" mb={2}>
        Stars settlement
      </Heading>
      <Text fontSize="sm" color="text.secondary" mb={4}>
        What each bot took over a period. Settlement itself is manual — these are
        the numbers to settle from.
      </Text>

      <Flex gap={3} align="flex-end" wrap="wrap" mb={4}>
        <FormControl maxW="200px">
          <FormLabel htmlFor="settlement-from" fontSize="sm">
            From
          </FormLabel>
          <Input
            id="settlement-from"
            type="date"
            size="sm"
            value={fromDraft}
            onChange={(e) => setFromDraft(e.target.value)}
          />
        </FormControl>
        <FormControl maxW="200px">
          <FormLabel htmlFor="settlement-to" fontSize="sm">
            To
          </FormLabel>
          <Input
            id="settlement-to"
            type="date"
            size="sm"
            value={toDraft}
            onChange={(e) => setToDraft(e.target.value)}
          />
        </FormControl>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() => setPeriod({ from: fromDraft, to: toDraft })}
          isLoading={settlementLoading}
        >
          Show period
        </Button>
      </Flex>

      {settlementLoading ? (
        <Flex justify="center" py={8}>
          <Spinner />
        </Flex>
      ) : settlementError ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {settlementError}
        </Alert>
      ) : (
        settlement && (
          <Box>
            <Text fontSize="sm" color="text.secondary" mb={2}>
              {settlement.period.date_from} to {settlement.period.date_to},
              inclusive.
            </Text>
            <Box overflowX="auto">
              <Table
                variant="simple"
                size="sm"
                aria-label="Stars settlement by bot"
              >
                <Thead>
                  <Tr>
                    <Th>Bot</Th>
                    <Th>Owner</Th>
                    <Th isNumeric>Stars</Th>
                    <Th isNumeric>USD</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {settlement.rows.map((row) => (
                    <Tr key={`${row.mirror_id ?? 'none'}-${row.bot_id ?? 'none'}`}>
                      <Td fontSize="sm">{botLabel(row)}</Td>
                      <Td fontSize="sm">{ownerLabel(row.owner_user_id)}</Td>
                      <Td isNumeric>{formatStars(row.stars_total)}</Td>
                      <Td isNumeric>{formatUsd(row.usd_total)}</Td>
                    </Tr>
                  ))}
                  {hasUnattributed && unattributed && (
                    /* Its own row, visually apart from the bots: these Stars
                       were taken before attribution shipped, or by a payment
                       that named no bot. Folding them into a bot would make
                       that bot's settlement wrong; omitting them would make the
                       period's total quietly wrong. */
                    <Tr bg="status.warningSubtle">
                      <Td fontSize="sm">
                        <Badge colorScheme="orange">Unattributed</Badge>
                      </Td>
                      <Td fontSize="sm">no bot recorded</Td>
                      <Td isNumeric fontWeight="semibold">
                        {formatStars(unattributed.stars_total)}
                      </Td>
                      <Td isNumeric fontWeight="semibold">
                        {formatUsd(unattributed.usd_total)}
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
            {hasUnattributed && (
              <Text fontSize="sm" color="text.secondary" mt={2}>
                Unattributed Stars carry no receiving bot — payments taken before
                attribution shipped, or ones that named none. They belong to no
                partner and are not owed to anyone by this report.
              </Text>
            )}
            {settlement.rows.length === 0 && !hasUnattributed && (
              <Text fontSize="sm" color="text.secondary" mt={4}>
                No Stars payments in this period.
              </Text>
            )}
          </Box>
        )
      )}
    </Box>
  );
};

export default StarsSettlementPanel;
